/**
 * CablingCanvas — Visual canvas for Patch Panel ↔ Wall Outlet structured cabling.
 *
 * Physical chain:  PC → [latiguillo] → Toma de pared → [cable en pared] → Panel port → [latiguillo] → Switch
 *
 * This canvas manages the WALL-SIDE cable only:
 *   Toma de pared ↔ Panel port  (stored as WallOutlet.panel_port_id FK, NOT a PortConnection)
 *
 * The SWITCH-SIDE cable (Panel port ↔ Switch port) is a regular PortConnection
 * managed in SwitchPortsTab; it is shown here as read-only context.
 */
import React, { useEffect, useState, useCallback } from 'react';
import api from '../services/api';

function CablingCanvas({ companyId }) {
  const [panels,       setPanels]       = useState([]);
  const [locations,    setLocations]    = useState([]);
  const [selPanelId,   setSelPanelId]   = useState('');
  const [panelPorts,   setPanelPorts]   = useState([]);
  const [outlets,      setOutlets]      = useState([]);
  const [filterLoc,    setFilterLoc]    = useState('');
  const [selPort,      setSelPort]      = useState(null);
  const [connectModal, setConnectModal] = useState(null);
  const [disconnModal, setDisconnModal] = useState(null);
  const [connectNotes, setConnectNotes] = useState('');
  const [saving,       setSaving]       = useState(false);
  const [loadingPorts, setLoadingPorts] = useState(false);
  const [loadingOuts,  setLoadingOuts]  = useState(false);
  const [error,        setError]        = useState(null);

  // Load panels + locations on mount
  useEffect(() => {
    if (!companyId) return;
    api.getElements(companyId, { element_type: 'panel' }).then(r => setPanels(r.data || [])).catch(() => {});
    api.getLocations('', companyId).then(r => setLocations(r.data || [])).catch(() => {});
  }, [companyId]);

  // Load outlets
  const loadOutlets = useCallback(async () => {
    if (!companyId) return;
    setLoadingOuts(true);
    try {
      const r = await api.getWallOutlets(companyId);
      setOutlets(r.data || []);
    } catch (e) { setError(e.message); }
    finally { setLoadingOuts(false); }
  }, [companyId]);
  useEffect(() => { loadOutlets(); }, [loadOutlets]);

  // Load panel ports
  const loadPorts = useCallback(async (pid) => {
    if (!pid) { setPanelPorts([]); return; }
    setLoadingPorts(true);
    try { const r = await api.getPanelPorts(pid, companyId); setPanelPorts(r.data || []); }
    catch (e) { setError(e.message); }
    finally { setLoadingPorts(false); }
  }, [companyId]);
  useEffect(() => { loadPorts(selPanelId); setSelPort(null); }, [selPanelId, loadPorts]);

  // Reload both after link change
  const reloadAll = useCallback(async () => {
    const [pr, or] = await Promise.all([
      selPanelId ? api.getPanelPorts(selPanelId, companyId) : Promise.resolve({ data: [] }),
      api.getWallOutlets(companyId),
    ]);
    if (selPanelId) setPanelPorts(pr.data || []);
    setOutlets(or.data || []);
  }, [selPanelId, companyId]);

  // Cross-highlight: for selected port, which outlet is physically wired to it?
  const highlightedOutletId = selPort?.wall_outlet?.id ?? null;

  // Visual styles per port — 4 states
  const portStyle = (port) => {
    const hasWall   = Boolean(port.wall_outlet);
    const hasSwitch = Boolean(port.connection);
    const isSel     = selPort?.id === port.id;
    if (!port.is_active) return { bg: 'var(--bg-toolbar)', dot: 'var(--text-muted)', bd: isSel ? 'var(--port-selected-bd)' : 'var(--slate-300)' };
    let dot;
    if (hasWall && hasSwitch) dot = 'var(--port-complete-dot)';
    else if (hasWall)         dot = 'var(--port-wall-dot)';
    else if (hasSwitch)       dot = 'var(--port-switch-dot)';
    else                      dot = 'var(--port-free-dot)';
    return { bg: isSel ? 'var(--port-selected-bg)' : hasWall && hasSwitch ? 'var(--port-complete-bg)' : hasWall ? 'var(--port-wall-bg)' : hasSwitch ? 'var(--port-switch-bg)' : 'var(--port-free-bg)', dot, bd: isSel ? 'var(--port-selected-bd)' : dot };
  };

  // Visual styles per outlet
  const outletStyle = (outlet) => {
    const isConn  = Boolean(outlet.panel_port_id);
    const isHL    = outlet.id === highlightedOutletId;
    const bg = isHL ? 'var(--port-selected-bg)' : isConn ? 'var(--port-wall-bg)' : 'var(--bg-toolbar)';
    const dot = isConn ? 'var(--port-wall-dot)' : 'var(--text-muted)';
    return { bg, dot, bd: isHL ? 'var(--port-selected-bd)' : dot };
  };

  // — Port click —
  // Wall-side cable (outlet ↔ panel port) is managed here.
  // Switch-side cable (panel port ↔ switch) is read-only in this canvas.
  const handlePortClick = (port) => {
    setError(null);
    if (port.wall_outlet) {
      // Has wall cable — show disconnect modal
      setDisconnModal({
        outletId: port.wall_outlet.id,
        sideA: port.label || `P${port.port_number}`,
        sideA_tag: panels.find(p => String(p.id) === selPanelId)?.name || 'Panel',
        sideB: port.wall_outlet.code,
        sideB_tag: port.wall_outlet.room
          ? `Toma · ${port.wall_outlet.room}${port.wall_outlet.floor ? ` (${port.wall_outlet.floor})` : ''}`
          : 'Toma de pared',
        switchInfo: port.connection
          ? `${port.connection.other_end?.element_name} · ${port.connection.other_end?.label || ''}`
          : null,
      });
    } else {
      // Free wall side — toggle selection (even if switch side is occupied)
      setSelPort(prev => prev?.id === port.id ? null : port);
    }
  };

  // — Outlet click —
  const handleOutletClick = (outlet) => {
    setError(null);
    if (selPort && !selPort.wall_outlet && !outlet.panel_port_id) {
      // Selected free port + free outlet → open connect modal
      setConnectModal({ port: selPort, outlet });
    } else if (!selPort && outlet.panel_port_id) {
      // No selection, outlet already wired → disconnect modal
      setDisconnModal({
        outletId: outlet.id,
        sideA: outlet.code,
        sideA_tag: outlet.room ? `Toma · ${outlet.room}` : 'Toma de pared',
        sideB: outlet.panel_port_label || `Puerto ${outlet.panel_port_id}`,
        sideB_tag: outlet.panel_name || 'Panel',
        switchInfo: null,
      });
    }
  };

  // — Connect: link outlet → panel port via FK (NOT a PortConnection) —
  const doConnect = async () => {
    if (!connectModal) return;
    setSaving(true); setError(null);
    try {
      await api.updateWallOutlet(connectModal.outlet.id, {
        panel_id:      parseInt(selPanelId),
        panel_port_id: connectModal.port.id,
        ...(connectNotes ? { notes: connectNotes } : {}),
      }, companyId);
      setConnectModal(null); setSelPort(null); setConnectNotes('');
      await reloadAll();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  // — Disconnect: clear panel_port_id FK —
  const doDisconn = async () => {
    if (!disconnModal) return;
    setSaving(true); setError(null);
    try {
      await api.updateWallOutlet(disconnModal.outletId, {
        panel_id:      null,
        panel_port_id: null,
      }, companyId);
      setDisconnModal(null); setSelPort(null);
      await reloadAll();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const filteredOutlets = filterLoc ? outlets.filter(o => String(o.location_id) === filterLoc) : outlets;
  const selPanelName    = panels.find(p => String(p.id) === selPanelId)?.name || '';
  const usedPorts       = panelPorts.filter(p => p.wall_outlet).length;

  return (
    <div className="cabling-fullpage" style={{ display: 'flex', flexDirection: 'column', background: 'var(--bg-card)' }}>

      {/* Toolbar */}
      <div style={{ padding: '0.7rem 1rem', background: 'var(--bg-card)', borderBottom: '1px solid var(--border)', display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 700, fontSize: '0.88rem', color: 'var(--text-primary)' }}>🔗 Canvas de Cableado Estructurado</span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <select value={selPanelId} onChange={e => setSelPanelId(e.target.value)}
            style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid var(--slate-300)', fontSize: '0.83rem', minWidth: 190, background: 'var(--bg-input)', color: 'var(--text-primary)' }}>
            <option value="">— Panel de parcheo —</option>
            {panels.map(p => <option key={p.id} value={p.id}>{p.name}{p.code ? ` (${p.code})` : ''}</option>)}
          </select>
          <select value={filterLoc} onChange={e => setFilterLoc(e.target.value)}
            style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid var(--slate-300)', fontSize: '0.83rem', minWidth: 140, background: 'var(--bg-input)', color: 'var(--text-primary)' }}>
            <option value="">Todas las ubicaciones</option>
            {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
        </div>
      </div>

      {/* Legend bar */}
      <div style={{ padding: '0.3rem 1rem', background: 'var(--bg-toolbar)', borderBottom: '1px solid var(--border)', display: 'flex', gap: 14, fontSize: '0.72rem', color: 'var(--text-muted)', alignItems: 'center', flexWrap: 'wrap' }}>
        {[['var(--port-free-dot)','Libre'],['var(--port-wall-dot)','Toma cableada'],['var(--port-switch-dot)','→ Switch'],['var(--port-complete-dot)','Completo (toma + switch)'],['var(--text-muted)','Inactivo']].map(([c,l]) => (
          <span key={l} style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <span style={{ width: 9, height: 9, borderRadius: '50%', background: c, display: 'inline-block' }} />{l}
          </span>
        ))}
        {selPanelId && (
          <span style={{ marginLeft: 'auto', color: 'var(--text-primary)', fontWeight: 600 }}>
            {selPanelName}: {usedPorts}/{panelPorts.length} puertos cableados a tomas
          </span>
        )}
        {selPort && !selPort.wall_outlet && (
          <span style={{ marginLeft: 8, background: 'var(--amber-50)', color: 'var(--port-uplink-dot)', padding: '2px 8px', borderRadius: 4, fontWeight: 700 }}>
            ✦ {selPort.label || `P${selPort.port_number}`} — clic en una toma libre para conectar
          </span>
        )}
      </div>

      {/* Two-column body */}
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden', minHeight: 0 }}>

        {/* LEFT — Panel ports */}
        <div style={{ width: '46%', borderRight: '2px solid var(--border)', overflowY: 'auto', padding: '0.75rem 1rem' }}>
          <div style={{ fontSize: '0.74rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>
            🗄 Puertos del panel
          </div>
          {!selPanelId && (
            <div style={{ textAlign: 'center', color: 'var(--text-muted)', marginTop: 60, fontSize: '0.85rem' }}>
              <div style={{ fontSize: '2rem', marginBottom: 10 }}>🗄</div>
              Selecciona un patch panel para ver sus puertos
            </div>
          )}
          {loadingPorts && <div style={{ textAlign: 'center', color: 'var(--text-muted)', marginTop: 60 }}>Cargando puertos…</div>}
          {selPanelId && !loadingPorts && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
              {panelPorts.map(port => {
                const { bg, dot, bd } = portStyle(port);
                return (
                  <button key={port.id} type="button" onClick={() => handlePortClick(port)}
                    title={
                      port.wall_outlet && port.connection
                        ? `🏢 ${port.wall_outlet.code} | 🔌 → ${port.connection.other_end?.element_name}`
                        : port.wall_outlet
                        ? `🏢 ${port.wall_outlet.code}${port.wall_outlet.room ? ' · ' + port.wall_outlet.room : ''} — clic para desconectar`
                        : port.connection
                        ? `🔌 → ${port.connection.other_end?.element_name} (latiguillo gestionado en Switch)`
                        : 'Libre — clic para seleccionar'
                    }
                    style={{ width: 66, minHeight: 64, borderRadius: 7, border: `2px solid ${bd}`, background: bg, cursor: 'pointer', padding: '4px 3px', textAlign: 'center', transition: 'all 0.12s' }}>
                    <div style={{ fontSize: '0.68rem', fontWeight: 700, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{port.label || `P${port.port_number}`}</div>
                    <div style={{ width: 10, height: 10, borderRadius: '50%', background: dot, margin: '2px auto' }} />
                    {port.wall_outlet && (
                      <div style={{ fontSize: '0.56rem', color: 'var(--wall-accent)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 60, margin: '0 auto' }}>
                        🏢 {port.wall_outlet.code}
                      </div>
                    )}
                    {port.connection && (
                      <div style={{ fontSize: '0.55rem', color: 'var(--accent)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 60, margin: '0 auto' }}>
                        🔌 {port.connection.other_end?.element_name}
                      </div>
                    )}
                  </button>
                );
              })}
              {panelPorts.length === 0 && (
                <div style={{ color: 'var(--text-muted)', fontSize: '0.82rem' }}>Este panel no tiene puertos configurados.</div>
              )}
            </div>
          )}
        </div>

        {/* RIGHT — Wall outlets */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '0.75rem 1rem' }}>
          <div style={{ fontSize: '0.74rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>
            🏢 Tomas de pared{filteredOutlets.length > 0 && <span style={{ fontWeight: 400 }}> ({filteredOutlets.length})</span>}
            {selPort && !selPort.wall_outlet && (
              <span style={{ marginLeft: 8, background: 'var(--green-50)', color: 'var(--green-600)', padding: '2px 7px', borderRadius: 4, fontSize: '0.72rem', fontWeight: 600, textTransform: 'none' }}>
                Clic en una toma libre →
              </span>
            )}
          </div>
          {loadingOuts && <div style={{ textAlign: 'center', color: 'var(--text-muted)', marginTop: 60 }}>Cargando …</div>}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
            {filteredOutlets.map(outlet => {
              const { bg, dot, bd } = outletStyle(outlet);
              const isConn   = Boolean(outlet.panel_port_id);
              const canConn  = selPort && !selPort.wall_outlet && !isConn;
              const dimmed   = Boolean(selPort && !selPort.wall_outlet && isConn);
              return (
                <button key={outlet.id} type="button" onClick={() => handleOutletClick(outlet)}
                  title={isConn
                    ? `← ${outlet.panel_name} · ${outlet.panel_port_label || '?'}`
                    : 'Sin cablear a panel'}
                  style={{ width: 78, minHeight: 66, borderRadius: 7, border: `2px solid ${bd}`, background: bg, cursor: (canConn || (!selPort && isConn)) ? 'pointer' : 'default', padding: '5px 4px', textAlign: 'center', transition: 'all 0.12s', opacity: dimmed ? 0.38 : 1 }}>
                  <div style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{outlet.code}</div>
                  <div style={{ width: 10, height: 10, borderRadius: '50%', background: dot, margin: '2px auto' }} />
                  {outlet.room  && <div style={{ fontSize: '0.58rem', color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 72 }}>{outlet.room}</div>}
                  {outlet.floor && <div style={{ fontSize: '0.56rem', color: 'var(--text-muted)' }}>{outlet.floor}</div>}
                  {isConn && (
                    <div style={{ fontSize: '0.57rem', color: 'var(--wall-accent)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 72, marginTop: 1 }}>
                      {outlet.panel_name} · {outlet.panel_port_label}
                    </div>
                  )}
                </button>
              );
            })}
            {filteredOutlets.length === 0 && !loadingOuts && (
              <div style={{ color: 'var(--text-muted)', fontSize: '0.82rem', width: '100%', textAlign: 'center', marginTop: 60 }}>
                <div style={{ fontSize: '2rem', marginBottom: 10 }}>🏢</div>Sin tomas registradas{filterLoc ? ' en esta ubicación' : ''}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Connect Modal ── */}
      {connectModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'var(--modal-overlay)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={e => { if (e.target === e.currentTarget) { setConnectModal(null); setError(null); } }}>
            <div style={{ background: 'var(--bg-surface)', borderRadius: 14, padding: '1.75rem', width: 400, boxShadow: 'var(--shadow-lg)', border: '1px solid var(--slate-300)' }}>
            <h3 style={{ margin: '0 0 0.75rem', fontSize: '1.05rem', color: 'var(--text-primary)' }}>📃 Cablear toma de pared → puerto de panel</h3>
            <div style={{ background: 'rgba(139,92,246,.12)', borderRadius: 8, padding: '0.75rem 1rem', marginBottom: '1rem', fontSize: '0.82rem', lineHeight: 2, color: 'var(--text-primary)', border: '1px solid rgba(139,92,246,.25)' }}>
              <div><span style={{ color: 'var(--text-muted)' }}>Panel · Puerto:</span> <strong>{selPanelName} · {connectModal.port.label || `P${connectModal.port.port_number}`}</strong></div>
              <div><span style={{ color: 'var(--text-muted)' }}>Toma de pared:</span> <strong>{connectModal.outlet.code}</strong>{connectModal.outlet.room ? ` · ${connectModal.outlet.room}` : ''}{connectModal.outlet.floor ? ` (${connectModal.outlet.floor})` : ''}</div>
              {connectModal.port.connection && (
                <div style={{ marginTop: 4, paddingTop: 4, borderTop: '1px solid rgba(139,92,246,.3)', color: '#c4b5fd', fontSize: '0.77rem' }}>
                  🔌 Puerto ya conectado a switch: <strong>{connectModal.port.connection.other_end?.element_name}</strong>
                </div>
              )}
            </div>
            <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', margin: '0 0 0.9rem' }}>
              Esto registra el cable de pared permanente que une esta toma con el panel.
              El latiguillo de patch panel al switch se gestiona desde la pestaña del switch.
            </p>
            {error && <div style={{ color: 'var(--error-text)', fontSize: '0.8rem', marginBottom: '0.75rem', background: 'var(--error-bg)', padding: '0.5rem', borderRadius: 6 }}>{error}</div>}
            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4 }}>Notas <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(opcional)</span></label>
            <input type="text" value={connectNotes} onChange={e => setConnectNotes(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && doConnect()} placeholder="Ej: cable en canaleta, planta 1…"
              style={{ width: '100%', marginBottom: '1.25rem', padding: '0.45rem', borderRadius: 6, border: '1px solid var(--slate-300)', fontSize: '0.85rem', boxSizing: 'border-box', background: 'var(--bg-input)', color: 'var(--text-primary)' }} />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => { setConnectModal(null); setError(null); setConnectNotes(''); }} disabled={saving}
                style={{ padding: '0.5rem 1rem', borderRadius: 7, border: '1px solid var(--slate-300)', background: 'var(--bg-input)', cursor: 'pointer', fontSize: '0.85rem', color: 'var(--text-primary)' }}>Cancelar</button>
              <button onClick={doConnect} disabled={saving}
                style={{ padding: '0.5rem 1.2rem', borderRadius: 7, border: 'none', background: saving ? 'var(--wall-accent)' : 'var(--port-wall-dot)', color: '#fff', fontWeight: 700, cursor: saving ? 'wait' : 'pointer', fontSize: '0.85rem' }}>
                {saving ? 'Guardando…' : '📃 Cablear'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Disconnect Modal ── */}
      {disconnModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'var(--modal-overlay)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={e => { if (e.target === e.currentTarget) { setDisconnModal(null); setError(null); } }}>
          <div style={{ background: 'var(--bg-surface)', borderRadius: 14, padding: '1.75rem', width: 400, boxShadow: 'var(--shadow-lg)', border: '1px solid var(--slate-300)' }}>
            <h3 style={{ margin: '0 0 0.75rem', fontSize: '1.05rem', color: 'var(--text-primary)' }}>✂ Cable de pared (toma ↔ panel)</h3>
            <div style={{ background: 'var(--bg-input)', borderRadius: 8, padding: '0.65rem 0.9rem', marginBottom: '0.75rem', fontSize: '0.82rem', lineHeight: 1.9, color: 'var(--text-primary)', border: '1px solid var(--border)' }}>
              <div><span style={{ color: 'var(--text-muted)' }}>{disconnModal.sideA_tag}:</span> <strong>{disconnModal.sideA}</strong></div>
              <div><span style={{ color: 'var(--text-muted)' }}>{disconnModal.sideB_tag}:</span> <strong>{disconnModal.sideB}</strong></div>
              {disconnModal.switchInfo && (
                <div style={{ marginTop: 4, paddingTop: 4, borderTop: '1px solid var(--border)', color: 'var(--node-self-sub)', fontSize: '0.77rem' }}>
                  🔌 Switch conectado: {disconnModal.switchInfo}
                </div>
              )}
            </div>
            <p style={{ fontSize: '0.77rem', color: 'var(--text-muted)', margin: '0 0 1rem' }}>Desconectar eliminará la asignación del cable de pared. El latiguillo hacia el switch no se verá afectado.</p>
            {error && <div style={{ color: 'var(--error-text)', fontSize: '0.8rem', marginBottom: '0.75rem', background: 'var(--error-bg)', padding: '0.5rem', borderRadius: 6 }}>{error}</div>}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => { setDisconnModal(null); setError(null); }} disabled={saving}
                style={{ padding: '0.5rem 1rem', borderRadius: 7, border: '1px solid var(--slate-300)', background: 'var(--bg-input)', cursor: 'pointer', fontSize: '0.85rem', color: 'var(--text-primary)' }}>Cerrar</button>
              <button onClick={doDisconn} disabled={saving}
                style={{ padding: '0.5rem 1.2rem', borderRadius: 7, border: 'none', background: saving ? 'var(--text-muted)' : 'var(--btn-danger)', color: '#fff', fontWeight: 700, cursor: saving ? 'wait' : 'pointer', fontSize: '0.85rem' }}>
                {saving ? 'Desconectando…' : '✂ Desconectar cable de pared'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CablingCanvas;
